/**
 *  mpc-sibling-swap — the Style (sibling) swatches and the Size row switch the product IN PLACE (owner ruling V11).
 *
 *  Distinctive renders each sibling colour/size as a plain <a href> to another product
 *  (snippets/product-siblings.liquid), so a click reloads the whole page. This file turns that
 *  click into: fetch the sibling's page, swap every section inside <main>, push the URL. The link
 *  itself is untouched, so no JS, a modifier/middle click, a failed fetch, or a page whose layout
 *  differs all end in the ordinary navigation — the swap is an enhancement, never the only path.
 *
 *  2026-11-02 speed pass (the switch used to wait ~0.3–0.5s on a full-page fetch with no feedback):
 *   - every Size link (and the first few Style swatches) is prefetched once the page is idle, so most
 *     taps swap from cache; hover/touch/focus still prefetch anything not yet loaded;
 *   - the tap shows at once: the chosen button is selected, the Size label updates and the gallery
 *     dims until the swap lands;
 *   - the incoming product's first photo is preloaded as soon as its page arrives;
 *   - cached pages stay fresh for 10 minutes (was 60s) and are reused across back-and-forth switching;
 *   - a page WITH Judge.me reviews no longer forces a full reload: the reviews are server-rendered and
 *     only hidden until Judge.me's script runs, so after the swap they are revealed directly. They show
 *     without the sort menu, so a product whose reviews run past the first page still navigates.
 *
 *  Rules:
 *   - product pages only, links inside <main> only (quick view and featured-product keep navigating);
 *   - the fetched <main> must carry the SAME section ids in the SAME order (same template), or we
 *     navigate instead of guessing;
 *   - <modal-dialog> elements move themselves to <body> on connect (app.js), so the OLD product's
 *     sticky add-to-cart modal would survive the swap with the same id as the new one — and
 *     getElementById would hand back the old product's form. They are removed before the swap;
 *   - Back/Forward after a swap reloads the page: the server is the truth, no DOM history kept;
 *   - scripts inside swapped sections are NOT re-executed: the theme is custom elements (they
 *     upgrade on insertion) and its assets are already loaded. So a page that needs a load-time
 *     script navigates instead: any <script src> this document never loaded (an app block).
 */
(function () {
  if (window.MpcSiblingSwap) return;

  const MAIN_ID = 'main-content';
  const LINK = '.variations--siblings a[href]';
  const SIZE_LINK = '.mpc-size-siblings a[href]';
  const STYLE_LINK = '.product-form__input--siblings a[href]';
  const STYLE_PREFETCH = 4; // style swatches can run to dozens; only warm the first few
  const FRESH_MS = 10 * 60 * 1000;
  const cache = new Map(); // url -> { at, promise }
  const preloaded = new Set();
  let busy = false;
  let queued = null;
  let swapped = false;

  const saveData = () => {
    const c = navigator.connection;
    return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
  };

  // Warm the incoming product's first gallery photo so it is ready when the swap lands.
  const preloadImage = (html) => {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const img = doc.querySelector(`#${MAIN_ID} .product-images__slide img, #${MAIN_ID} .product-single__media img`);
      if (!img) return doc;
      const srcset = img.getAttribute('data-srcset') || img.getAttribute('srcset');
      const src = img.getAttribute('data-src') || img.getAttribute('src');
      const key = srcset || src;
      if (!key || preloaded.has(key)) return doc;
      preloaded.add(key);
      const current = document.querySelector(`#${MAIN_ID} .product-images__slide.is-selected img, #${MAIN_ID} .product-single__media img`);
      const warm = new Image();
      warm.decoding = 'async';
      if (srcset) {
        warm.sizes = (current && current.sizes && current.sizes !== 'auto' && current.sizes) || `${Math.min(window.innerWidth, 725)}px`;
        warm.srcset = srcset;
      }
      if (src && !src.startsWith('data:')) warm.src = src;
      return doc;
    } catch (error) {
      return null;
    }
  };

  const load = (url) => {
    const hit = cache.get(url);
    if (hit && Date.now() - hit.at < FRESH_MS) return hit.promise;
    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (html) preloadImage(html);
        return html;
      })
      .catch(() => null);
    cache.set(url, { at: Date.now(), promise });
    // A failed fetch should not be remembered.
    promise.then((html) => {
      if (!html && cache.get(url)?.promise === promise) cache.delete(url);
    });
    return promise;
  };

  const sameUrl = (href) => {
    try {
      const a = new URL(href, window.location.href);
      return a.pathname === window.location.pathname;
    } catch (error) {
      return false;
    }
  };

  const prefetchAll = () => {
    if (!document.body.classList.contains('template-product') || saveData()) return;
    const main = document.getElementById(MAIN_ID);
    if (!main) return;
    const urls = [];
    main.querySelectorAll(SIZE_LINK).forEach((a) => urls.push(a.href));
    Array.from(main.querySelectorAll(STYLE_LINK))
      .filter((a) => !a.closest('label')?.classList.contains('active'))
      .slice(0, STYLE_PREFETCH)
      .forEach((a) => urls.push(a.href));
    // Stagger so the prefetches never compete with the page's own first paint.
    Array.from(new Set(urls))
      .filter((url) => !sameUrl(url))
      .forEach((url, index) => setTimeout(() => load(url), index * 150));
  };

  const whenIdle = (fn) => {
    const run = () => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 2000 }) : setTimeout(fn, 300));
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };

  const sections = (main) => Array.from(main.children).filter((el) => el.classList.contains('shopify-section'));

  // Judge.me ships its review widget server-rendered but hidden until its load-time script runs
  // (which cannot be re-run). Reveal the incoming widget directly.
  const revealReviews = (root) => {
    root.querySelectorAll('.jdgm-temp-hiding-style').forEach((style) => style.remove());
    root.querySelectorAll('.jdgm-widget').forEach((widget) => widget.classList.add('jdgm--done-setup-widget'));
    root.querySelectorAll('.jdgm-legacy-widget-content, .jdgm-rev-widg').forEach((el) => {
      if (el.style.display === 'none') el.style.display = '';
    });
    try {
      if (window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') window.jdgm.batchRenderBadges();
    } catch (error) {
      /* badges are cosmetic */
    }
  };

  // Returns true when the page was swapped; false means "navigate normally".
  const swap = (url, html) => {
    if (!html) return false;
    const next = new DOMParser().parseFromString(html, 'text/html');
    const nextMain = next.getElementById(MAIN_ID);
    const main = document.getElementById(MAIN_ID);
    if (!nextMain || !main) return false;

    const current = sections(main);
    const incoming = sections(nextMain);
    if (!current.length || current.length !== incoming.length) return false;
    if (current.some((section, index) => section.id !== incoming[index].id)) return false;

    // Revealed reviews are static (no sort, filter or "load more"), so only swap in place when every
    // review is already in the page; a product with more reviews than one page navigates instead.
    const reviews = nextMain.querySelector('.jdgm-rev-widg');
    if (reviews && Number(reviews.getAttribute('data-number-of-reviews')) > reviews.querySelectorAll('.jdgm-rev').length) return false;

    // Generic net: a <script src> inside the incoming <main> that this document never loaded is an
    // app block or asset we have not run — navigate instead.
    const loaded = new Set(Array.from(document.querySelectorAll('script[src]')).map((script) => script.src.split('?')[0]));
    const foreign = Array.from(nextMain.querySelectorAll('script[src]')).some((script) => !loaded.has(script.src.split('?')[0]));
    if (foreign) return false;

    // The old product's body-level modals (sticky add to cart) must not outlive it.
    nextMain.querySelectorAll('modal-dialog[id]').forEach((modal) => {
      document.querySelectorAll(`body > modal-dialog[id="${modal.id}"]`).forEach((stale) => stale.remove());
    });
    document.body.classList.remove('overflow-hidden');

    current.forEach((section, index) => {
      section.innerHTML = incoming[index].innerHTML;
    });
    revealReviews(main);

    if (next.title) document.title = next.title;
    if (!swapped) window.history.replaceState({ mpcSibling: true }, '', window.location.href);
    window.history.pushState({ mpcSibling: true }, '', url);
    swapped = true;

    main.querySelector('.variations--siblings label.active a, .mpc-size-siblings input:checked + label')?.focus({ preventScroll: true });
    document.dispatchEvent(new CustomEvent('mpc:sibling-swapped', { detail: { url } }));
    return true;
  };

  // Show the choice straight away, before the new product arrives.
  const showPending = (link) => {
    const label = link.closest('label');
    const fieldset = link.closest('fieldset');
    if (!label || !fieldset) return;
    if (fieldset.classList.contains('mpc-size-siblings')) {
      const input = label.htmlFor && document.getElementById(label.htmlFor);
      if (input) input.checked = true;
      const value = fieldset.querySelector('.form__label__value');
      if (value) value.textContent = link.textContent.trim();
    } else {
      fieldset.querySelectorAll('label.active').forEach((el) => el.classList.remove('active'));
      label.classList.add('active');
    }
    document.getElementById(MAIN_ID)?.classList.add('mpc-swapping');
  };

  const clearPending = () => document.getElementById(MAIN_ID)?.classList.remove('mpc-swapping');

  const go = (link) => {
    busy = true;
    const url = link.href;
    const fieldset = link.closest('fieldset');
    fieldset?.setAttribute('aria-busy', 'true');
    showPending(link);
    load(url).then((html) => {
      let done = false;
      if (!queued) {
        try {
          done = swap(url, html);
        } catch (error) {
          done = false;
        }
      }
      busy = false;
      fieldset?.removeAttribute('aria-busy');
      if (queued) {
        // The shopper tapped another option while this one loaded: go straight to that one.
        const nextLink = queued;
        queued = null;
        go(nextLink);
        return;
      }
      clearPending();
      if (!done) window.location.href = url;
    });
  };

  const siblingLink = (event) => {
    if (!document.body.classList.contains('template-product')) return null;
    const link = event.target.closest ? event.target.closest(LINK) : null;
    return link && link.closest(`#${MAIN_ID}`) ? link : null;
  };

  const onClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = siblingLink(event);
    if (!link) return;
    event.preventDefault();
    if (sameUrl(link.href)) return;
    if (busy) {
      queued = link;
      showPending(link);
      return;
    }
    go(link);
  };

  const onIntent = (event) => {
    const link = siblingLink(event);
    if (link && !sameUrl(link.href)) load(link.href);
  };

  const style = document.createElement('style');
  style.textContent =
    `#${MAIN_ID}.mpc-swapping .product-images,#${MAIN_ID}.mpc-swapping .product-single__media{opacity:.55;transition:opacity .12s ease}` +
    `#${MAIN_ID}.mpc-swapping .variations--siblings{cursor:progress}`;
  document.head.appendChild(style);

  document.addEventListener('click', onClick);
  document.addEventListener('mouseover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('mpc:sibling-swapped', () => whenIdle(prefetchAll));
  whenIdle(prefetchAll);
  // After a swap the DOM no longer matches the history entry being restored: ask the server.
  window.addEventListener('popstate', () => {
    if (swapped) window.location.reload();
  });

  window.MpcSiblingSwap = { swap, onClick, load };
})();
